import type { ProductConfigBuilder } from "../product-types";
import { AURORA, DECO, breadcrumb } from "./shared";
import heroLifestyle from "@/assets/products/tv6-ultra/hero-lifestyle-clean.jpg";
import performanceOpaque from "@/assets/products/tv6-ultra/performance-opaque.jpg";
import performanceTransparent from "@/assets/products/tv6-ultra/performance-transparent.jpg";
import ports from "@/assets/products/tv6-ultra/ports-closeup-clean.jpg";
import remote from "@/assets/products/tv6-ultra/remote-clean.jpg";
import softwareLifestyle from "@/assets/products/tv6-ultra/software-lifestyle-clean.jpg";

export const buildTv6UltraConfig: ProductConfigBuilder = (ctx) => {
  const m = ctx.messages;
  const u = ctx.urls;

  return {
    mainClassName: "overflow-x-hidden",
    breadcrumb: breadcrumb(ctx, "inext TV6 Ultra"),
    sections: [
      {
        kind: "hero",
        tone: "gradient",
        decorations: [],
        primaryGlow: false,
        eyebrow: m.tv6UltraV2HeroEyebrow,
        headline: m.tv6UltraV2HeroHeadline,
        sub: m.tv6UltraV2HeroSub,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
        compareHref: u.compare,
        compareLabel: m.tv6UltraCompareLabel,
        media: {
          type: "image",
          src: heroLifestyle,
          alt: m.tv6UltraHeroImageAlt,
          width: 1200,
          height: 800,
          shadowGlow: true,
        },
      },
      {
        kind: "glance",
        bgClassName: "bg-card/40",
        stats: [
          [m.tv6UltraV2GlanceValue1, m.tv6UltraV2GlanceLabel1],
          [m.tv6UltraV2GlanceValue2, m.tv6UltraV2GlanceLabel2],
          [m.tv6UltraV2GlanceValue3, m.tv6UltraV2GlanceLabel3],
          [m.tv6UltraV2GlanceValue4, m.tv6UltraV2GlanceLabel4],
        ],
      },
      {
        kind: "intro",
        name: "TV6 Ultra",
        paras: [m.tv6UltraV2IntroPara1, m.tv6UltraV2IntroPara2],
      },
      {
        kind: "performance",
        bgClassName: "bg-muted/20",
        layout: "imageRight",
        eyebrow: m.tv6UltraV2PerformanceEyebrow,
        title: m.tv6UltraV2PerformanceTitle,
        body: m.tv6UltraV2PerformanceBody,
        accent: { style: "pill", value: m.tv6UltraV2PerformanceAccent },
        media: {
          type: "reveal",
          opaque: performanceOpaque,
          transparent: performanceTransparent,
          width: 1200,
          height: 800,
          label: m.tv6UltraHardwareRevealLabel,
          alt: m.tv6UltraHardwareRevealImageAlt,
          auroraClassName: AURORA.perfReveal,
        },
      },
      {
        kind: "software",
        bgClassName: "bg-muted/30",
        layout: "imageLeft",
        eyebrow: m.tv6UltraV2SoftwareEyebrow,
        title: m.tv6UltraV2SoftwareTitle,
        body: m.tv6UltraV2SoftwareBody,
        cards: [
          [m.tv6UltraV2SoftwareCustomTitle1, m.tv6UltraV2SoftwareCustomBody1],
          [m.tv6UltraV2SoftwareCustomTitle2, m.tv6UltraV2SoftwareCustomBody2],
          [m.tv6UltraV2SoftwareCustomTitle3, m.tv6UltraV2SoftwareCustomBody3],
        ],
        media: {
          type: "image",
          src: softwareLifestyle,
          alt: m.productSoftwareLifestyleImageAlt,
          width: 1200,
          height: 675,
          loading: "lazy",
          shadowGlow: true,
          auroraClassName: AURORA.softwareImage,
        },
      },
      {
        kind: "connectivity",
        decorations: [{ kind: "grid", className: DECO.grid30 }],
        layout: "imageRight",
        eyebrow: m.tv6UltraV2ConnectivityEyebrow,
        title: m.tv6UltraV2ConnectivityTitle,
        caption: { style: "paragraph", value: m.tv6UltraV2ConnectivityCaption },
        media: {
          type: "image",
          src: ports,
          alt: m.tv6UltraPortsImageAlt,
          width: 1200,
          height: 800,
          loading: "lazy",
        },
      },
      {
        kind: "remote",
        decorations: [{ kind: "auroraBlob", className: DECO.remoteAuroraBlob }],
        layout: "imageLeft",
        eyebrow: m.tv6UltraV2RemoteEyebrow,
        title: m.tv6UltraV2RemoteTitle,
        body: m.tv6UltraV2RemoteBody,
        bullets: [
          m.tv6UltraV2RemoteFeatureBt,
          m.tv6UltraV2RemoteFeatureMic,
          m.tv6UltraV2RemoteFeatureTvPower,
          m.tv6UltraV2RemoteFeatureFav,
        ],
        appLink: { href: u.remoteApp, label: m.remoteAppProductCallout },
        note: {
          title: m.tv6RemoteBtNoteTitle,
          body: m.tv6UltraV2RemoteNoteBody,
          link: {
            href: `${u.accessories}?category=remotes#accessory-list`,
            label: m.tv6RemoteBtNoteLink,
          },
        },
        media: {
          type: "image",
          src: remote,
          alt: m.tv6UltraRemoteImageAlt,
          width: 1200,
          height: 800,
          loading: "lazy",
          auroraClassName: AURORA.remoteImage,
        },
      },
      {
        kind: "alcatraz",
        pill: m.operationsStatPill,
        title: m.tv6UltraAlcatrazTitle,
        body: m.tv6UltraAlcatrazBody,
        ctaHref: u.alcatraz,
        ctaLabel: m.tv6UltraAlcatrazCta,
      },
      {
        kind: "specs",
        bgClassName: "bg-muted/10",
        title: m.tv6UltraV2SpecsTitle,
        note: m.packageNote,
        groups: [
          {
            title: m.tv6UltraV2SpecsGroupHardware,
            rows: [
              [m.tv6UltraSpecChipset, "Allwinner H618"],
              [m.tv6UltraSpecCpu, m.productSpecCpuQuadCoreArm],
              [m.tv6UltraSpecGpu, "ARM Mali-G31 MP2"],
              [m.tv6UltraSpecRam, "4 GB DDR3"],
              [m.tv6UltraSpecStorage, "32 GB eMMC"],
              [m.tv6UltraSpecDimensions, "103 × 103 × 20 mm"],
              [m.tv6UltraSpecPower, "5 V / 2 A"],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupConnectivity,
            rows: [
              [m.tv6UltraSpecHdmi, m.productSpecHdmiCableIncluded],
              [m.tv6UltraSpecEthernet, m.productSpecEthernet100],
              [m.tv6UltraSpecWifi, m.productSpecWifiDualBandAc],
              [m.tv6UltraSpecBluetooth, "Bluetooth 5.0"],
              [m.tv6UltraSpecUsb, m.productSpecUsbHost],
              [m.tv6UltraSpecCardReader, "microSD / TF"],
              [m.tv6UltraSpecAudioOut, m.productSpecAudioSpdifRca],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupMedia,
            rows: [
              [m.tv6UltraSpecMaxRes, m.tv6UltraSpecMaxResValue],
              [
                m.tv6UltraSpecVideoCodecs,
                "H.265 (4K@60) · VP9 P2 (4K@30) · H.264 (4K@30) · MPEG-1/2/4 · AVS · WMV9 · VP8 · Xvid/DivX",
              ],
              [
                m.tv6UltraSpecVideoFormats,
                "AVI · MKV · ISO · VOB · DAT · MPG/MPEG · MP4 · MOV · TS · XVID · ASF · FLV · WMV",
              ],
              [
                m.tv6UltraSpecAudioFormats,
                "MP3 · WMA · AAC · WAV · OGG · MIDI · FLAC · APE · ALAC · M4A",
              ],
              [m.tv6UltraSpecImageFormats, "JPG/JPEG · HD JPEG · TIFF · PNG · BMP · GIF"],
              [m.tv6UltraSpec3d, m.tv6UltraSpec3dValue],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupSoftware,
            rows: [
              [m.tv6UltraSpecOs, "Android 12 (AOSP)"],
              [m.tv6UltraSpecDms, "inext Alcatraz™ DMS"],
              [m.tv6UltraSpecTr069, m.tv6UltraSpecTr069Yes],
              [m.tv6UltraSpecFilesystems, "exFAT · FAT32 · NTFS · EXT · HFS+"],
              [m.tv6UltraSpecLanguages, m.tv6UltraSpecLanguagesValue],
              [m.tv6UltraSpecInternet, m.productSpecInternetFeatures],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupPackage,
            rows: [
              ["·", m.tv6UltraPackageRemote],
              ["·", m.tv6UltraPackagePower],
              ["·", m.tv6UltraPackageHdmi],
              ["·", m.tv6UltraPackageBatteries],
              ["·", m.tv6UltraPackageGuide],
            ],
          },
        ],
      },
      {
        kind: "cta",
        title: m.tv6UltraCtaTitle,
        body: m.tv6UltraCtaBody,
        compareHref: u.compare,
        compareLabel: m.tv6UltraCompareLabel,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
      },
    ],
  };
};
